import {
  signalStoreFeature,
  type SignalStoreFeature,
  type,
  withMethods,
} from '@ngrx/signals';
import type { RxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap, tap, type Observable } from 'rxjs';

/** The line sub-resource calls of one parent (a setup charge, a service charge,
 *  a quote), plus how the parent's own store learns the recomputed totals. */
export interface ChargeLinesApi<Parent, Create, Patch> {
  create: (identity: string, body: Create) => Observable<Parent>;
  update: (identity: string, lineIdentity: string, body: Patch) => Observable<Parent>;
  destroy: (identity: string, lineIdentity: string) => Observable<unknown>;
  syncParent: (parent: Parent) => void;
  /** Re-reads the parent AND syncs it into its store. */
  refreshParent: (identity: string) => Observable<Parent>;
}

/**
 * The three line mutations every line store repeats. Goes after
 * `withCursorPagination<Line, string>`, whose `load(parentIdentity)` it reuses.
 *
 * POST and PATCH answer the WHOLE parent with its totals recomputed server-side —
 * never the line — so the rows come from a reload and the parent goes to its own
 * store, which is what the listing behind the lines modal reads. DELETE answers
 * 204 with no body, so the parent has to be re-read.
 *
 * `apiFactory` is a FACTORY, run inside `withMethods` (an injection context) so
 * it can `inject` the generated service and the parent store. The explicit
 * `SignalStoreFeature` return type is REQUIRED: see `withCursorPagination`.
 */
export function withChargeLines<Parent, Create, Patch>(
  apiFactory: () => ChargeLinesApi<Parent, Create, Patch>,
): SignalStoreFeature<
  { state: object; props: object; methods: { load: RxMethod<string> } },
  {
    state: object;
    props: object;
    methods: {
      createLine: (identity: string, body: Create) => Observable<Parent>;
      updateLine: (
        identity: string,
        lineIdentity: string,
        body: Patch,
      ) => Observable<Parent>;
      deleteLine: (identity: string, lineIdentity: string) => Observable<Parent>;
    };
  }
> {
  return signalStoreFeature(
    { methods: type<{ load: RxMethod<string> }>() },
    withMethods((store) => {
      const api = apiFactory();
      const settle = (identity: string) =>
        tap((parent: Parent) => {
          api.syncParent(parent);
          store.load(identity);
        });
      return {
        createLine: (identity: string, body: Create) =>
          api.create(identity, body).pipe(settle(identity)),
        updateLine: (identity: string, lineIdentity: string, body: Patch) =>
          api.update(identity, lineIdentity, body).pipe(settle(identity)),
        deleteLine: (identity: string, lineIdentity: string) =>
          api.destroy(identity, lineIdentity).pipe(
            switchMap(() => api.refreshParent(identity)),
            tap(() => {
              store.load(identity);
            }),
          ),
      };
    }),
  );
}
